import { useEffect } from 'react';
import { useChatStore } from '../stores/chatStore';

/**
 * Hook to handle communication from the parent window when embedded.
 * Specifically listens for 'END_CHAT' messages.
 * @param endSession Function to end the current chat session.
 * @param actualMerchantId The determined merchant ID for context.
 */
export function useEmbeddedEvents(endSession: (reason?: string) => void, actualMerchantId: string | null) {
  useEffect(() => {
    const handleMessageEvent = (event: MessageEvent) => {
      console.log('[MattressAI] Received message:', event.data);

      // Handle END_CHAT messages from parent window
      if (event.data && event.data.type === 'END_CHAT') {
        console.log('[MattressAI] Received END_CHAT message with reason:', event.data.reason);
        
        try {
          // End the chat session with the given reason
          const reason = event.data.reason || 'user_closed';
          
          // Get the merchant ID from URL parameters or subdomain if available
          const urlParams = new URLSearchParams(window.location.search);
          const merchantIdParam = urlParams.get('merchantId');
          const hostname = window.location.hostname;
          const subdomain = hostname.split('.')[0];
          
          // Log the merchant ID being used for debugging
          console.log('[MattressAI] Using merchant identifier:', {
            fromParams: merchantIdParam,
            fromSubdomain: subdomain,
            fromStore: actualMerchantId
          });
          
          // Force the store to use the correct merchant ID, not the thread ID
          // Note: Directly setting store state from a hook is discouraged.
          // Consider if this merchant ID logic should be handled within endSession or store itself.
          if (merchantIdParam) {
            useChatStore.setState({ actualMerchantId: merchantIdParam });
          } else if (subdomain && !['localhost', 'www'].includes(subdomain)) {
            useChatStore.setState({ actualMerchantId: subdomain });
          }
          
          endSession(reason);
          console.log('[MattressAI] Session ended due to message from parent');
        } catch (error) {
          console.error('[MattressAI] Error handling END_CHAT message:', error);
        }
      }
    };

    // Add the event listener
    window.addEventListener('message', handleMessageEvent);

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessageEvent);
    };
  }, [endSession, actualMerchantId]); // Dependencies for the effect
} 